/* ==========================================================================
   LifeCare — product.js
   PDP runtime, loaded only on product templates (defer).
   Depends on theme.js (CartAPI, announce patterns, lc-slider).
   ========================================================================== */

(() => {
  'use strict';

  const moneyFormat = window.LcSettings?.moneyFormat || '${{amount}}';

  /**
   * @param {number} cents
   * @returns {string} formatted money using the shop's money format
   */
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  };

  /* ------ <lc-variant-picker> ---------------------------------------------------
     Reads the product variants JSON embedded by main-product.liquid, resolves
     the selected variant on every option change, and updates: hidden id input,
     prices, availability, buy buttons, sticky bar, media, and the URL. */

  class LcVariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-lc-variants-json]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.section = this.closest('[data-lc-product]');
      this.liveRegion = this.section?.querySelector('[data-lc-variant-live]');
      this.addEventListener('change', this.#onChange.bind(this));
    }

    get selectedOptions() {
      return [...this.querySelectorAll('fieldset, .lc-product__option')].map((container) => {
        return (
          container.querySelector('input:checked')?.value ||
          container.querySelector('select')?.value
        );
      });
    }

    #onChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((option, i) => option === options[i])
      );
      this.#updateLabels();
      this.#updateState(variant);
      document.dispatchEvent(
        new CustomEvent('lc:variant:change', { detail: { variant } })
      );
    }

    #updateLabels() {
      for (const container of this.querySelectorAll('fieldset, .lc-product__option')) {
        const value = container.querySelector('input:checked')?.value;
        const label = container.querySelector('[data-lc-option-value]');
        if (label && value) label.textContent = value;
      }
    }

    #updateState(variant) {
      const scope = this.section || document;

      for (const input of scope.querySelectorAll('[data-lc-variant-id]')) {
        input.value = variant?.id ?? '';
        input.disabled = !variant;
      }

      for (const price of scope.querySelectorAll('[data-lc-price]')) {
        this.#renderPrice(price, variant);
      }

      for (const button of scope.querySelectorAll('[data-lc-buy-button], [data-action-checkout]')) {
        const label = button.querySelector('.lc-button__label');
        const available = Boolean(variant?.available);
        button.toggleAttribute('disabled', !available);
        if (label) {
          label.textContent = !variant
            ? button.dataset.labelUnavailable
            : available
              ? button.dataset.labelAdd
              : button.dataset.labelSoldOut;
        }
      }

      for (const prepaidWidget of scope.querySelectorAll('.lc-prepaid-buy-widget')) {
        const prepaidPrice = prepaidWidget.querySelector('[data-lc-prepaid-price]');
        const prepaidCompare = prepaidWidget.querySelector('[data-lc-prepaid-compare]');
        const prepaidBadge = prepaidWidget.querySelector('[data-lc-prepaid-save-badge]');
        const prepaidSaveAmount = prepaidWidget.querySelector('[data-lc-prepaid-save-amount]');

        if (prepaidPrice && variant) {
          prepaidPrice.textContent = formatMoney(variant.price);
          const onSale = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
          if (prepaidCompare) {
            prepaidCompare.style.display = onSale ? '' : 'none';
            if (onSale) prepaidCompare.textContent = formatMoney(variant.compare_at_price);
          }
          if (prepaidBadge) {
            const saveCents = onSale ? (variant.compare_at_price - variant.price) : 0;
            prepaidBadge.style.display = saveCents > 0 ? '' : 'none';
            if (prepaidSaveAmount && saveCents > 0) {
              prepaidSaveAmount.textContent = formatMoney(saveCents);
            }
          }
        }
      }

      if (variant?.featured_media?.id) {
        const media = scope.querySelector(
          `[data-lc-media-id="${variant.featured_media.id}"]`
        );
        media?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
      }

      if (variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }

      if (this.liveRegion && variant) {
        this.liveRegion.textContent = '';
        requestAnimationFrame(() => {
          this.liveRegion.textContent = `${variant.title} — ${formatMoney(variant.price)}`;
        });
      }
    }

    #renderPrice(el, variant) {
      const current = el.querySelector('[data-lc-price-current]');
      const compare = el.querySelector('[data-lc-price-compare]');
      if (!variant || !current) return;
      current.textContent = formatMoney(variant.price);
      const onSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      el.classList.toggle('lc-price--sale', Boolean(onSale));
      if (compare) {
        compare.hidden = !onSale;
        if (onSale) {
          const s = compare.querySelector('s') || compare;
          s.textContent = formatMoney(variant.compare_at_price);
        }
      }
    }
  }
  customElements.define('lc-variant-picker', LcVariantPicker);

  /* ------ <lc-media-gallery> -----------------------------------------------------
     Scroll-snap main track + thumbnail rail kept in sync both ways. */

  class LcMediaGallery extends HTMLElement {
    #observer = null;

    connectedCallback() {
      this.track = this.querySelector('[data-lc-slider-track]');
      this.thumbs = [...this.querySelectorAll('[data-lc-thumb]')];
      if (!this.track) return;

      for (const thumb of this.thumbs) {
        thumb.addEventListener('click', () => {
          this.querySelector(
            `[data-lc-media-id="${thumb.dataset.lcThumb}"]`
          )?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
        });
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.intersectionRatio < 0.6) continue;
            this.#setActive(entry.target.dataset.lcMediaId);
          }
        },
        { root: this.track, threshold: 0.6 }
      );
      for (const slide of this.track.children) this.#observer.observe(slide);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
    }

    #setActive(mediaId) {
      for (const thumb of this.thumbs) {
        const active = thumb.dataset.lcThumb === mediaId;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          thumb.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
      }
    }
  }
  customElements.define('lc-media-gallery', LcMediaGallery);

  /* ------ <lc-sticky-atc> ---------------------------------------------------------
     Shows the floating add-to-cart bar once the buy box scrolls out of view. */

  class LcStickyAtc extends HTMLElement {
    #observer = null;

    connectedCallback() {
      const anchor = document.querySelector('[data-lc-buy-box]');
      if (!anchor) return;
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle(
            'is-visible',
            !entry.isIntersecting && entry.boundingClientRect.top < 0
          );
        },
        { threshold: 0 }
      );
      this.#observer.observe(anchor);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
    }
  }
  customElements.define('lc-sticky-atc', LcStickyAtc);

  /* ------ Record the visit for "Recently viewed" ------------------------------------- */

  const productId = document.querySelector('[data-lc-product]')?.dataset.lcProductId;
  if (productId && window.LcRecentlyViewed) {
    window.LcRecentlyViewed.track(productId);
  }
})();
